import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { BuildingStructureBox } from "@/utils/supabase";

/**
 * 사용자 정의 프리셋 인터페이스
 */
export interface CustomPreset {
  id: string;
  name: string;
  description: string;
  structure: BuildingStructureBox[];
  createdAt: number;
}

/**
 * 프리셋 스토어 상태
 */
interface PresetStoreState {
  // 사용자 정의 프리셋 목록
  customPresets: CustomPreset[];
  
  // 프리셋 추가
  addPreset: (preset: Omit<CustomPreset, "id" | "createdAt">) => string;
  
  // 프리셋 삭제
  removePreset: (id: string) => void;
  
  // 프리셋 업데이트
  updatePreset: (id: string, updates: Partial<Omit<CustomPreset, "id" | "createdAt">>) => void;
  
  // 프리셋 가져오기
  getPreset: (id: string) => CustomPreset | undefined;
  
  // 모든 프리셋 가져오기
  getAllPresets: () => CustomPreset[];
}

/**
 * 사용자 정의 프리셋 스토어
 * localStorage에 저장하여 영구 보존
 */
export const usePresetStore = create<PresetStoreState>()(
  persist(
    (set, get) => ({
      customPresets: [],
      
      addPreset: (preset) => {
        const id = `custom-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
        const newPreset: CustomPreset = {
          ...preset,
          id,
          createdAt: Date.now(),
        };
        
        set((state) => ({
          customPresets: [...state.customPresets, newPreset],
        }));
        
        return id;
      },
      
      removePreset: (id) => {
        set((state) => ({
          customPresets: state.customPresets.filter((p) => p.id !== id),
        }));
      },
      
      updatePreset: (id, updates) => {
        set((state) => ({
          customPresets: state.customPresets.map((p) =>
            p.id === id ? { ...p, ...updates } : p
          ),
        }));
      },
      
      getPreset: (id) => {
        return get().customPresets.find((p) => p.id === id);
      },
      
      getAllPresets: () => {
        return get().customPresets;
      },
    }),
    {
      name: "building-custom-presets",
      storage: createJSONStorage(() => localStorage),
    }
  )
);
